import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, TextField, Button, Chip, Grid, Alert,
  CircularProgress, Snackbar, MenuItem, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import apiService from '../services/api.service';

interface ReportOrder {
  salesorderid: number; sonumber: string; cardcode: string; cardname: string | null;
  currency: string | null; status: string; docdate: string | null; submitteddate: string | null;
  totalnetamount: number | string; sapdocnum: string | null; createdbyname: string | null;
}
interface Bucket { key: string; orderCount: number; totalNet: number; }
interface Summary {
  orderCount: number; totalNet: number; customerCount: number;
  bySalesperson: Bucket[]; byStatus: Bucket[]; currencies: string[];
}
interface ReportData { from: string; to: string; status: string | null; orders: ReportOrder[]; summary: Summary; }

const STATUSES = ['Draft', 'PendingApproval', 'Approved', 'Rejected', 'Failed', 'Cancelled'];

/** Local calendar date — not toISOString, which is UTC and can be yesterday. */
function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const fmt = (n: number | string) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const dateOnly = (v: string | null) => (v ? String(v).slice(0, 10) : '—');

function SalesOrderReport() {
  const companyId = useMemo(
    () => Number(localStorage.getItem('selectedCompanyId') || '') || undefined, []);
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState(todayIso());
  const [status, setStatus] = useState('');
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res: any = await apiService.getSalesOrderReport(companyId, from, to, status || undefined);
      setData(res.data);
    } catch (e: any) {
      setToast(e.response?.data?.message || 'Failed to load the report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, from, to, status]);

  useEffect(() => { void load(); }, [load]);

  const exportExcel = () => {
    if (!data || data.orders.length === 0) return;
    const orders = XLSX.utils.json_to_sheet(data.orders.map((o) => ({
      'SO Number': o.sonumber,
      Date: dateOnly(o.docdate || o.submitteddate),
      Customer: o.cardname || o.cardcode,
      'Card Code': o.cardcode,
      Salesperson: o.createdbyname || 'Unassigned',
      Status: o.status,
      'SAP Doc': o.sapdocnum || '',
      Currency: o.currency || '',
      Total: Number(o.totalnetamount || 0),
    })));
    orders['!cols'] = [{ wch: 16 }, { wch: 12 }, { wch: 34 }, { wch: 14 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 9 }, { wch: 14 }];

    const bySp = XLSX.utils.json_to_sheet(data.summary.bySalesperson.map((b) => ({
      Salesperson: b.key, Orders: b.orderCount, Total: b.totalNet,
    })));
    const byStatus = XLSX.utils.json_to_sheet(data.summary.byStatus.map((b) => ({
      Status: b.key, Orders: b.orderCount, Total: b.totalNet,
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, orders, 'Orders');
    XLSX.utils.book_append_sheet(wb, bySp, 'By Salesperson');
    XLSX.utils.book_append_sheet(wb, byStatus, 'By Status');
    const name = data.from === data.to ? data.from : `${data.from}_to_${data.to}`;
    XLSX.writeFile(wb, `Sales_Orders_${name}.xlsx`);
  };

  if (!companyId) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Alert severity="info">Select a company (top bar) to run the sales order report.</Alert>
      </Box>
    );
  }

  const mixedCurrency = (data?.summary.currencies.length ?? 0) > 1;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" component="h1" gutterBottom>Sales Order Report</Typography>

      <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
          <TextField
            label="From" type="date" size="small" value={from}
            onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To" type="date" size="small" value={to}
            onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }}
            error={to < from}
            helperText={to < from ? 'Earlier than From — the report uses a single day' : ' '}
          />
          <TextField
            select label="Status" size="small" value={status} sx={{ minWidth: 170 }}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>
            Run
          </Button>
          <Button
            variant="outlined" startIcon={<DownloadIcon />} onClick={exportExcel}
            disabled={!data || data.orders.length === 0}
          >
            Export Excel
          </Button>
        </Stack>
      </Paper>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <>
          {mixedCurrency && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This window contains more than one currency ({data.summary.currencies.join(', ')}).
              The totals below add them together and are not meaningful as a single figure —
              filter by a narrower period, or read the per-order amounts.
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Orders', value: String(data.summary.orderCount) },
              { label: 'Customers', value: String(data.summary.customerCount) },
              {
                label: 'Total value',
                value: `${fmt(data.summary.totalNet)}${data.summary.currencies.length === 1 ? ` ${data.summary.currencies[0]}` : ''}`,
              },
            ].map((c) => (
              <Grid item xs={12} sm={4} key={c.label}>
                <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                  <Typography variant="h6">{c.value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {([
              { title: 'By salesperson', rows: data.summary.bySalesperson, head: 'Salesperson' },
              { title: 'By status', rows: data.summary.byStatus, head: 'Status' },
            ]).map((sec) => (
              <Grid item xs={12} md={6} key={sec.title}>
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ p: 1.5, pb: 0.5 }}>{sec.title}</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>{sec.head}</TableCell>
                        <TableCell align="right">Orders</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {sec.rows.length === 0 && (
                          <TableRow><TableCell colSpan={3}>
                            <Typography variant="caption" color="text.secondary">Nothing in this period.</Typography>
                          </TableCell></TableRow>
                        )}
                        {sec.rows.map((b) => (
                          <TableRow key={b.key}>
                            <TableCell>{b.key}</TableCell>
                            <TableCell align="right">{b.orderCount}</TableCell>
                            <TableCell align="right">{fmt(b.totalNet)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ p: 1.5, pb: 0.5 }}>
              Orders {data.from === data.to ? `on ${data.from}` : `from ${data.from} to ${data.to}`}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead><TableRow>
                  <TableCell>SO Number</TableCell><TableCell>Date</TableCell><TableCell>Customer</TableCell>
                  <TableCell>Salesperson</TableCell><TableCell>Status</TableCell><TableCell>SAP Doc</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.orders.length === 0 && (
                    <TableRow><TableCell colSpan={7}>
                      <Typography variant="caption" color="text.secondary">No sales orders in this period.</Typography>
                    </TableCell></TableRow>
                  )}
                  {data.orders.map((o) => (
                    <TableRow key={o.salesorderid}>
                      <TableCell sx={{ fontWeight: 600 }}>{o.sonumber}</TableCell>
                      <TableCell>{dateOnly(o.docdate || o.submitteddate)}</TableCell>
                      <TableCell>{o.cardname || o.cardcode} ({o.cardcode})</TableCell>
                      <TableCell>{o.createdbyname || 'Unassigned'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={o.status}
                          color={o.status === 'Approved' ? 'success' : o.status === 'Failed' || o.status === 'Rejected' ? 'error'
                            : o.status === 'PendingApproval' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{o.sapdocnum || '—'}</TableCell>
                      <TableCell align="right">{fmt(o.totalnetamount)} {o.currency || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </>
      )}

      <Snackbar
        open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}

export default SalesOrderReport;
